import React, { useEffect, useMemo, useState } from 'react';
import {
  HbrOption,
  HbrSelect,
  HbrTab,
  HbrTabGroup,
  HbrTabPanel,
} from '@harbor/elements-react-standalone';
import { arrowsClockwise } from '@harbor/elements/icons';
import StbButton from '../../security-toolbox-ui/StbButton';
import StbModal from '../../security-toolbox-ui/StbModal';
import DcloudLinkStatusPanel from './DcloudLinkStatusPanel';
import DcloudSessionsTable from './DcloudSessionsTable';
import DcloudScheduleSessionModal, { DcloudScheduleTarget } from './DcloudScheduleSessionModal';
import DcloudEditSessionModal from './DcloudEditSessionModal';
import {
  useDcloudLinkStatus,
  useDcloudPreferredDataCenter,
  useDcloudSessionHistory,
  useDcloudSessions,
  useEndDcloudSession,
} from '../../../hooks/dcloud-hooks';
import {
  DCLOUD_DATA_CENTERS,
  DcloudSession,
  formatDcloudDate,
  partitionDcloudSessions,
} from '../../../support/dcloudService';
import { asSingleValue } from '../../../support/utils';

export type DcloudSessionManagerProps = {
  /** Demo to pre-select when the schedule form opens. */
  initialTarget?: DcloudScheduleTarget | null;
  /** Open the schedule form immediately, e.g. when launched from a demo tile. */
  autoOpenSchedule?: boolean;
  /** 'drawer' drops the outer card chrome for hosts that already provide it. */
  variant?: 'inline' | 'drawer';
  className?: string;
};

type TabKey = 'current' | 'previous';

const DcloudSessionManager: React.FC<DcloudSessionManagerProps> = ({
  initialTarget = null,
  autoOpenSchedule = false,
  variant = 'inline',
  className = '',
}) => {
  const { dataCenter, setDataCenter } = useDcloudPreferredDataCenter();

  const [tab, setTab] = useState<TabKey>('current');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleTarget, setScheduleTarget] = useState<DcloudScheduleTarget | null>(initialTarget);
  const [editSession, setEditSession] = useState<DcloudSession | null>(null);
  const [endTarget, setEndTarget] = useState<DcloudSession | null>(null);

  const { data: linkStatus } = useDcloudLinkStatus();
  const linkRequired = Boolean(
    linkStatus?.dcloud_user_token?.enabled && !linkStatus?.dcloud_user_token?.usable,
  );

  const {
    data: sessionsResult,
    isLoading: loadingSessions,
    isFetching: fetchingSessions,
    isError: sessionsFailed,
    error: sessionsError,
    refetch: refetchSessions,
  } = useDcloudSessions(dataCenter);

  const {
    data: history,
    isLoading: loadingHistory,
    isError: historyFailed,
    error: historyError,
    refetch: refetchHistory,
  } = useDcloudSessionHistory(dataCenter, { enabled: tab === 'previous' });

  const endSession = useEndDcloudSession(dataCenter);

  useEffect(() => {
    if (!autoOpenSchedule) return;

    setScheduleTarget(initialTarget);
    setScheduleOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenSchedule, initialTarget?.demoId, initialTarget?.searchText]);

  const { current, previous } = useMemo(
    () => partitionDcloudSessions(sessionsResult?.sessions || []),
    [sessionsResult],
  );

  /**
   * /history and /sessions overlap: a session that just ended can appear in
   * both. Sessions win because they carry the expanded demo details.
   */
  const previousSessions = useMemo(() => {
    const seen = new Set<string>();
    const rows: DcloudSession[] = [];

    [...previous, ...(history || [])].forEach((session, index) => {
      const key = session.id != null ? `id:${session.id}` : `row:${session.name}-${index}`;

      if (seen.has(key)) return;

      seen.add(key);
      rows.push(session);
    });

    return rows;
  }, [previous, history]);

  const handleEnd = async () => {
    if (!endTarget?.id) return;

    try {
      await endSession.mutateAsync(endTarget.id);
    } catch {
      // Surfaced through endSession.error below.
      return;
    }

    setEndTarget(null);
  };

  const handleRefresh = () => {
    refetchSessions();

    if (tab === 'previous') refetchHistory();
  };

  const errorMessage = (error: unknown): string | null =>
    (error as Error)?.message || 'Unable to reach dCloud.';

  const body = (
    <div className="flex flex-col gap-4">
      <DcloudLinkStatusPanel authSource={sessionsResult?.authSource} />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-[220px]">
          <HbrSelect
            value={dataCenter}
            size="small"
            onHbr-change={(evt: any) => setDataCenter(asSingleValue(evt.target.value))}
          >
            {DCLOUD_DATA_CENTERS.map((dc) => (
              <HbrOption value={dc.value} key={dc.value}>
                {dc.label}
              </HbrOption>
            ))}
            <div slot="label">Data center</div>
            <div slot="help-text">Sessions exist only in the data center they were booked in.</div>
          </HbrSelect>
        </div>

        <div className="flex items-center gap-2">
          <StbButton
            role="default"
            size="small"
            icon={arrowsClockwise}
            loading={fetchingSessions}
            onClick={async () => handleRefresh()}
          >
            Refresh
          </StbButton>

          <StbButton
            role="add"
            size="small"
            onClick={async () => {
              setScheduleTarget(null);
              setScheduleOpen(true);
            }}
          >
            Schedule session
          </StbButton>
        </div>
      </div>

      {linkRequired && (
        <p className="m-0 text-sm text-amber-800">
          Sign in to dCloud above to see and manage your own sessions. Until then this list shows
          what the shared service account can see.
        </p>
      )}

      <HbrTabGroup primary>
        <HbrTab
          slot="nav"
          panel="current"
          active={tab === 'current'}
          onClick={() => setTab('current')}
        >
          {`Active & scheduled${current.length ? ` (${current.length})` : ''}`}
        </HbrTab>

        <HbrTab
          slot="nav"
          panel="previous"
          active={tab === 'previous'}
          onClick={() => setTab('previous')}
        >
          Previous
        </HbrTab>

        <HbrTabPanel name="current">
          {tab === 'current' && (
            <DcloudSessionsTable
              sessions={current}
              loading={loadingSessions}
              error={sessionsFailed ? errorMessage(sessionsError) : null}
              emptyMessage={`No active or scheduled sessions in ${dataCenter.toUpperCase()}.`}
              variant="current"
              busySessionId={endSession.isPending ? endTarget?.id ?? null : null}
              onEdit={(session) => setEditSession(session)}
              onEnd={(session) => setEndTarget(session)}
            />
          )}
        </HbrTabPanel>

        <HbrTabPanel name="previous">
          {tab === 'previous' && (
            <DcloudSessionsTable
              sessions={previousSessions}
              loading={loadingSessions || loadingHistory}
              error={
                sessionsFailed
                  ? errorMessage(sessionsError)
                  : historyFailed
                    ? errorMessage(historyError)
                    : null
              }
              emptyMessage={`No previous sessions in ${dataCenter.toUpperCase()}.`}
              variant="previous"
              onScheduleAgain={(session) => {
                setScheduleTarget({
                  demoId: session.demoId,
                  name: session.demoName || session.name,
                  searchText: session.demoName || session.name,
                });
                setScheduleOpen(true);
              }}
            />
          )}
        </HbrTabPanel>
      </HbrTabGroup>
    </div>
  );

  return (
    <div className={className}>
      {variant === 'drawer' ? (
        body
      ) : (
        <section className="rounded-md border border-gray-200 bg-white p-4">
          <h3 className="mt-0 mb-3 font-semibold">dCloud sessions</h3>
          {body}
        </section>
      )}

      <DcloudScheduleSessionModal
        open={scheduleOpen}
        dataCenter={dataCenter}
        target={scheduleTarget}
        onClose={() => setScheduleOpen(false)}
        onScheduled={() => setTab('current')}
      />

      <DcloudEditSessionModal
        open={Boolean(editSession)}
        dataCenter={dataCenter}
        session={editSession}
        onClose={() => setEditSession(null)}
      />

      <StbModal
        open={Boolean(endTarget)}
        role="delete"
        title="End this dCloud session?"
        submitLabel="End session"
        suppressCloseOnSubmit
        onSubmit={handleEnd}
        onClose={async () => setEndTarget(null)}
        apiResponseSection={
          endSession.isError ? (
            <p className="m-0 text-sm text-amber-800">
              {endSession.error?.message || 'dCloud could not end this session.'}
            </p>
          ) : undefined
        }
      >
        <div className="flex flex-col gap-2 text-sm">
          <p className="m-0">
            {`"${endTarget?.name || 'This session'}" will be torn down immediately and its reservation released. dCloud has no way to undo this.`}
          </p>
          {endTarget?.stop && (
            <p className="m-0 text-gray-600">
              {`It was scheduled to run until ${formatDcloudDate(endTarget.stop)}.`}
            </p>
          )}
        </div>
      </StbModal>
    </div>
  );
};

export default DcloudSessionManager;
